import React, { useState, useEffect, useCallback, useRef } from 'react';
import './PortailConstructeur.css';
import { STATUTS as STATUTS_TAKATA } from './Takata';

import { texteTheme } from '../utils/theme';
const API_URL = process.env.REACT_APP_API_URL || '';

const TYPES_CONTACT = {
  appel_sortant:  { label: 'Appel sortant',  picto: '📞' },
  appel_entrant:  { label: 'Appel entrant',  picto: '📲' },
  sms:            { label: 'SMS',            picto: '💬' },
  email:          { label: 'E-mail',         picto: '✉️' },
  courrier:       { label: 'Courrier',        picto: '📬' },
  visite:         { label: 'Visite',          picto: '🏢' },
  no_show:        { label: 'No Show',         picto: '🚫' },
  autres:         { label: 'Autres',          picto: '📝' },
};

const RESULTATS_CONTACT = {
  joint:              { label: 'Contacté',          color: '#22c55e' },
  messagerie:         { label: 'Messagerie',         color: '#f97316' },
  pas_de_reponse:     { label: 'Pas de réponse',     color: '#94a3b8' },
  mauvais_numero:     { label: 'Mauvais numéro',     color: '#ef4444' },
  refuse:             { label: 'Refus',              color: '#dc2626' },
  accepte_rdv:        { label: 'Accepte RDV',        color: '#3b82f6' },
  courrier_envoye:    { label: 'Envoyé',             color: '#8b5cf6' },
  distribue:          { label: 'Distribué',          color: '#22c55e' },
  npai:               { label: 'NPAI',               color: '#ef4444' },
  changement_proprio: { label: 'Chgt propriétaire',  color: '#f97316' },
  non_reclame:        { label: 'Non réclamé',         color: '#dc2626' },
  autres:             { label: 'Autres',             color: '#6b7280' },
};

// Source unique des statuts — même convention que PortailComptesAdmin.js : la
// liste vient de Takata.js, jamais d'une copie locale. Celle qu'on entretenait
// ici avait justement divergé du schéma (`takata_dossiers_statut_check`) : il lui
// manquait 'rdv_propose', 'reforme' et 'vole', et elle gardait 'rdv_pris', retiré
// de la contrainte depuis. Ces statuts s'affichaient donc en slug brut.
const STATUTS = Object.fromEntries(
  STATUTS_TAKATA.map(s => [s.value, { label: s.label, color: s.color }])
);

// Les couleurs de statut peuvent être un token CSS (`var(--text-muted)`) et pas
// seulement un hexa : concaténer '22' pour obtenir un fond translucide ne marche
// que sur un hexa, sinon la déclaration est invalide et le fond disparaît.
const fondBadge = (couleur) => (String(couleur).startsWith('#') ? couleur + '22' : 'var(--bg-muted)');

const STATUTS_CAMPAGNE = {
  termine: { label: 'Terminé',  color: '#22c55e' },
  partiel: { label: 'Partiel',  color: '#f97316' },
  a_faire: { label: 'À faire',  color: '#ef4444' },
};

const TYPES_RDV   = { atelier: 'Atelier', domicile: 'Domicile' };
const STATUTS_RDV = {
  propose:  { label: 'Proposé',  color: '#f97316' },
  confirme: { label: 'Confirmé', color: '#3b82f6' },
  honore:   { label: 'Honoré',   color: '#22c55e' },
  no_show:  { label: 'No Show',  color: '#94a3b8' },
  // « Reporté » (2026-09-09) et « Refusé » manquaient : un RDV dans ces états
  // s'affichait avec son slug brut sur le portail constructeur.
  reporte:  { label: 'Reporté',  color: '#8b5cf6' },
  refuse:   { label: 'Refusé',   color: '#f59e0b' },
  annule:   { label: 'Annulé',   color: '#ef4444' },
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function apiFetch(path, options = {}) {
  const token = localStorage.getItem('constructeur_token');
  return fetch(`${API_URL}/api/portail-constructeur${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  }).then(async res => {
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erreur');
    return data;
  });
}

// ─── Composant principal ─────────────────────────────────────────────────────

function PortailConstructeur() {
  const [token, setToken] = useState(localStorage.getItem('constructeur_token'));
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem('constructeur_session')); } catch { return null; }
  });

  const handleLogin = (tok, info) => {
    localStorage.setItem('constructeur_token', tok);
    localStorage.setItem('constructeur_session', JSON.stringify(info));
    setToken(tok);
    setSession(info);
  };

  const handleLogout = () => {
    localStorage.removeItem('constructeur_token');
    localStorage.removeItem('constructeur_session');
    setToken(null);
    setSession(null);
  };

  if (!token || !session) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return <DossiersView session={session} onLogout={handleLogout} />;
}

// ─── Login ───────────────────────────────────────────────────────────────────

function LoginForm({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await apiFetch('/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      onLogin(data.token, {
        nom: data.nom,
        marques: data.marques,
        statuts_visibles: data.statuts_visibles,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pc-login-container">
      <div className="pc-login-card">
        <div className="pc-login-brand">
          <h1>Portail Constructeur</h1>
          <span>Suivi campagne rappel Takata</span>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="pc-error">{error}</div>}
          <div className="pc-field">
            <label htmlFor="pc-username">Identifiant</label>
            <input id="pc-username" type="text" value={username} onChange={e => setUsername(e.target.value)} autoFocus required />
          </div>
          <div className="pc-field">
            <label htmlFor="pc-password">Mot de passe</label>
            <input id="pc-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
          </div>
          <button type="submit" className="pc-login-btn" disabled={loading}>
            {loading ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Vue Dossiers ────────────────────────────────────────────────────────────

// Cases à cocher dans un menu déroulant — pas de composant multi-select partagé
// dans le CRM, on reste sur les classes communes (.page-select, .filter-chip).
function MultiSelectStatuts({ label, options, selection, onChange }) {
  const [ouvert, setOuvert] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ouvert) return;
    const dehors = e => { if (ref.current && !ref.current.contains(e.target)) setOuvert(false); };
    document.addEventListener('mousedown', dehors);
    return () => document.removeEventListener('mousedown', dehors);
  }, [ouvert]);

  const bascule = (val) => {
    onChange(selection.includes(val) ? selection.filter(v => v !== val) : [...selection, val]);
  };

  return (
    <div className="pc-multiselect" ref={ref}>
      <button
        type="button"
        className={`page-select${selection.length ? ' active-filter' : ''}`}
        onClick={() => setOuvert(o => !o)}
      >
        {selection.length ? `${label} (${selection.length})` : label}
      </button>
      {ouvert && (
        <div className="pc-multiselect-menu">
          {options.map(([value, { label: texte }]) => (
            <label key={value} className="pc-multiselect-option">
              <input type="checkbox" checked={selection.includes(value)} onChange={() => bascule(value)} />
              <span>{texte}</span>
            </label>
          ))}
          {selection.length > 0 && (
            <button type="button" className="pc-multiselect-clear" onClick={() => onChange([])}>
              Tout décocher
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DossiersView({ session, onLogout }) {
  const [dossiers, setDossiers] = useState([]);
  const [counts, setCounts] = useState({ par_statut: {}, par_campagne: {} });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statutsSel, setStatutsSel] = useState([]);
  const [campagneSel, setCampagneSel] = useState([]);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [sortCol, setSortCol] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedDossier, setSelectedDossier] = useState(null);

  // Filtres et tri sont appliqués côté serveur : les compteurs portent ainsi sur
  // le même ensemble que le tableau, y compris au-delà des lignes affichées.
  const loadDossiers = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search) qs.set('search', search);
      if (statutsSel.length) qs.set('statuts', statutsSel.join(','));
      if (campagneSel.length) qs.set('statuts_campagne', campagneSel.join(','));
      if (dateDebut) qs.set('date_debut', dateDebut);
      if (dateFin) qs.set('date_fin', dateFin);
      qs.set('sort', sortCol);
      qs.set('dir', sortDir);

      const data = await apiFetch(`/takata?${qs.toString()}`);
      setDossiers(data.dossiers);
      setCounts(data.counts || { par_statut: {}, par_campagne: {} });
      setTotal(data.total ?? data.dossiers.length);
    } catch (err) {
      if (err.message.includes('Token') || err.message.includes('expiré')) onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout, search, statutsSel, campagneSel, dateDebut, dateFin, sortCol, sortDir]);

  // Recherche débattue pour ne pas requêter à chaque frappe.
  useEffect(() => {
    const t = setTimeout(loadDossiers, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadDossiers, search]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortIndicator = (col) => (sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const thTri = (col, texte, style) => (
    <th className="pc-th-sort" style={style} onClick={() => toggleSort(col)} title="Trier">
      {texte}{sortIndicator(col)}
    </th>
  );

  const filtresActifs = search || statutsSel.length || campagneSel.length || dateDebut || dateFin;
  const reinitialiser = () => {
    setSearch(''); setStatutsSel([]); setCampagneSel([]); setDateDebut(''); setDateFin('');
  };

  return (
    <div className="pc-page">
      <header className="pc-header">
        <div className="pc-header-left">
          <h1>Portail Constructeur</h1>
          <span className="pc-header-info">{session.nom} — {session.marques.join(', ')}</span>
        </div>
        <button className="pc-logout-btn" onClick={onLogout}>Déconnexion</button>
      </header>

      <div className="page-container">
        <div className="pc-stats">
          <span>{total} dossier{total !== 1 ? 's' : ''}{filtresActifs ? ' (filtré)' : ''}</span>
          {Object.entries(STATUTS_CAMPAGNE).map(([cle, { label, color }]) => (
            <span key={cle} className="pc-stat-chip" style={{ background: fondBadge(color), color }}>
              {label} : {counts.par_campagne?.[cle] || 0}
            </span>
          ))}
        </div>

        <div className="page-toolbar">
          <input
            className="page-search"
            placeholder="🔍 Rechercher VIN / Immat…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <MultiSelectStatuts
            label="Statut"
            options={Object.entries(STATUTS)}
            selection={statutsSel}
            onChange={setStatutsSel}
          />
          <MultiSelectStatuts
            label="Statut campagne"
            options={Object.entries(STATUTS_CAMPAGNE)}
            selection={campagneSel}
            onChange={setCampagneSel}
          />
          <input
            type="date"
            className={`page-select${dateDebut ? ' active-filter' : ''}`}
            value={dateDebut}
            onChange={e => setDateDebut(e.target.value)}
            title="Mise à jour depuis le"
          />
          <input
            type="date"
            className={`page-select${dateFin ? ' active-filter' : ''}`}
            value={dateFin}
            onChange={e => setDateFin(e.target.value)}
            title="Mise à jour jusqu'au"
          />
          {filtresActifs && (
            <button type="button" className="page-select" onClick={reinitialiser}>Réinitialiser</button>
          )}
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                {thTri('vin', 'VIN / Immat')}
                {thTri('marque', 'Constructeur / Modèle')}
                {thTri('statut', 'Statut')}
                <th style={{ textAlign: 'center' }}>DRIV.</th>
                <th style={{ textAlign: 'center' }}>PASS.</th>
                {thTri('statut_campagne', 'Statut campagne')}
                {thTri('date_premier_contact', '1er contact')}
                {thTri('dernier_contact', 'Dernier contact')}
                {thTri('dernier_rdv', 'Date RDV')}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="10" style={{ textAlign: 'center', padding: 40 }}>Chargement…</td></tr>
              ) : dossiers.length === 0 ? (
                <tr><td colSpan="10" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Aucun dossier</td></tr>
              ) : dossiers.map(d => {
                const st = STATUTS[d.statut] || { label: d.statut, color: '#94a3b8' };
                const sc = STATUTS_CAMPAGNE[d.statut_campagne] || STATUTS_CAMPAGNE.a_faire;
                return (
                  <tr key={d.id} onClick={() => setSelectedDossier(d)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.85rem' }}>{d.vin}</div>
                      {d.immat && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 2 }}>{d.immat}</div>}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{d.marque}</div>
                      {d.modele && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{d.modele}</div>}
                    </td>
                    <td>
                      <span style={{ background: fondBadge(st.color), color: st.color, padding: '2px 10px', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {st.label}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {d.driv_airbag_replaced
                        ? <span style={{ color: 'light-dark(#22c55e, var(--color-success-dark))', fontWeight: 700 }}>✓</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {d.passenger_airbag_replaced
                        ? <span style={{ color: 'light-dark(#22c55e, var(--color-success-dark))', fontWeight: 700 }}>✓</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td>
                      <span style={{ background: fondBadge(sc.color), color: sc.color, padding: '2px 8px', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600 }}>
                        {sc.label}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{formatDate(d.date_premier_contact)}</td>
                    <td style={{ fontSize: '0.85rem' }}>{formatDate(d.dernier_contact)}</td>
                    <td style={{ fontSize: '0.85rem' }}>{formatDate(d.dernier_rdv)}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Voir le dossier" onClick={() => setSelectedDossier(d)}>👁️</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedDossier && (
        <DetailModal
          dossierId={selectedDossier.id}
          dossierResume={selectedDossier}
          onClose={() => setSelectedDossier(null)}
        />
      )}
    </div>
  );
}

// ─── Modale de détail — vue constructeur (même layout que DetailDossierModal) ──

const RGPD_REDACTED = <span style={{ color: 'var(--text-muted)', letterSpacing: '0.12em' }}>●●●●●●●●●●</span>;

function DetailModal({ dossierId, dossierResume, onClose }) {
  const [tab, setTab] = useState('journal');
  const [dossier, setDossier] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [rdvList, setRdvList] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [detailRes, contactsRes, rdvRes, docsRes] = await Promise.allSettled([
        apiFetch(`/takata/${dossierId}`),
        apiFetch(`/takata/${dossierId}/contacts`),
        apiFetch(`/takata/${dossierId}/rdv`),
        apiFetch(`/takata/${dossierId}/documents`),
      ]);
      if (detailRes.status === 'fulfilled')   setDossier(detailRes.value.dossier);
      if (contactsRes.status === 'fulfilled') setContacts(contactsRes.value.contacts);
      if (rdvRes.status === 'fulfilled')      setRdvList(rdvRes.value.rdv);
      if (docsRes.status === 'fulfilled')     setDocuments(docsRes.value.documents);
      setLoading(false);
    };
    load();
  }, [dossierId]);

  const d = dossier || dossierResume;
  const isGele = d?.statut === 'gele';
  const st = STATUTS[d?.statut] || { label: d?.statut, color: '#94a3b8' };
  const sc = STATUTS_CAMPAGNE[dossierResume.statut_campagne] || STATUTS_CAMPAGNE.a_faire;

  const TABS = [
    { id: 'journal',   label: `📋 Journal${!loading && contacts.length ? ` (${contacts.length})` : ''}` },
    { id: 'rdv',       label: `📅 RDV${!loading && rdvList.length ? ` (${rdvList.length})` : ''}` },
    { id: 'documents', label: `📎 Documents${!loading && documents.length ? ` (${documents.length})` : ''}` },
    { id: 'infos',     label: 'ℹ️ Infos' },
  ];

  return (
    <div className="modal-overlay">
      <div className="modal-content large takata-modal--large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="takata-modal-hd-left">
            <span className="takata-modal-title">Dossier #{dossierId}</span>
            {dossierResume.immat && <span className="takata-vin-chip">{dossierResume.immat}</span>}
            <span className="takata-badge" style={{ background: fondBadge(st.color), color: st.color, marginLeft: 8 }}>
              {isGele && '🔒 '}{st.label}
            </span>
            <span className="takata-badge" style={{ background: fondBadge(sc.color), color: sc.color }}>
              Campagne : {sc.label}
            </span>
          </div>
          <div className="takata-modal-hd-right">
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="takata-detail-summary">
          <div className="takata-summary-col">
            <div className="takata-summary-item"><span>Marque</span><strong>{d?.marque || '—'}</strong></div>
            <div className="takata-summary-item"><span>Modèle</span><strong>{d?.modele || '—'}</strong></div>
            <div className="takata-summary-item"><span>Immat</span><strong>{d?.immat || '—'}</strong></div>
          </div>
          <div className="takata-summary-col">
            <div className="takata-summary-item"><span>Client</span><strong>{RGPD_REDACTED}</strong></div>
            <div className="takata-summary-item"><span>Tél</span><strong>{RGPD_REDACTED}</strong></div>
          </div>
          <div className="takata-summary-col">
            <div className="takata-summary-item"><span>Concession</span><strong>{d?.concession_nom || '—'}</strong></div>
            <div className="takata-summary-item"><span>Créé le</span><strong>{formatDate(d?.created_at)}</strong></div>
            <div className="takata-summary-item"><span>1er contact</span><strong>{dossier?.date_premier_contact ? formatDate(dossier.date_premier_contact) : '—'}</strong></div>
          </div>
        </div>

        <div className="takata-detail-tabs">
          {TABS.map(t => (
            <button key={t.id} className={tab === t.id ? 'active' : ''} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="takata-detail-body">
          {loading ? (
            <div className="takata-loading" style={{ padding: 40, textAlign: 'center' }}>Chargement…</div>
          ) : (
            <>
              {tab === 'journal'   && <PortailJournalTab contacts={contacts} />}
              {tab === 'rdv'       && <PortailRdvTab rdvList={rdvList} />}
              {tab === 'documents' && <PortailDocumentsTab documents={documents} />}
              {tab === 'infos'     && <InfosTab dossier={dossier} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Onglet Journal (lecture seule) ──────────────────────────────────────────

// Masque la ligne "Coordonnées renseignées —" dans les notes [Webapp] (RGPD).
function renderNotes(notes) {
  if (!notes) return null;
  if (!notes.startsWith('[Webapp]')) return <div className="takata-timeline-notes">{notes}</div>;
  return (
    <div className="takata-timeline-notes">
      {notes.split('\n').map((line, i) => {
        if (line.startsWith('Coordonnées renseignées')) {
          return (
            <span key={i} style={{ display: 'block', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              🔒 Coordonnées client masquées (RGPD)
            </span>
          );
        }
        return line ? <span key={i} style={{ display: 'block' }}>{line}</span> : null;
      })}
    </div>
  );
}

function PortailJournalTab({ contacts }) {
  return (
    <div className="takata-journal">
      <div className="takata-journal-toolbar">
        <span className="takata-journal-count">{contacts.length} entrée{contacts.length !== 1 ? 's' : ''}</span>
      </div>
      {contacts.length === 0 ? (
        <div className="takata-empty-journal">Aucun contact enregistré.</div>
      ) : (
        <div className="takata-timeline">
          {contacts.map((c, i) => {
            const typeInfo = TYPES_CONTACT[c.type] || { label: c.type, picto: '📝' };
            const resultatInfo = RESULTATS_CONTACT[c.resultat] || { label: c.resultat, color: '#94a3b8' };
            return (
              <div key={i} className="takata-timeline-item">
                <div className="takata-timeline-dot" style={{ background: resultatInfo.color + '33' }}>
                  {typeInfo.picto}
                </div>
                <div className="takata-timeline-content">
                  <div className="takata-timeline-hd">
                    <span className="takata-timeline-type">{typeInfo.label}</span>
                    <span className="takata-timeline-resultat" style={{ color: texteTheme(resultatInfo.color) }}>
                      {resultatInfo.label}
                    </span>
                    {c.est_premier_contact && (
                      <span className="takata-badge" style={{ background: 'var(--color-success-light)', color: 'light-dark(var(--color-success), var(--color-success-dark))', fontSize: '0.75rem' }}>
                        🤝 Premier contact
                      </span>
                    )}
                    {(c.user_prenom || c.user_nom) && (
                      <span className="takata-timeline-agent">
                        {[c.user_prenom, c.user_nom].filter(Boolean).join(' ')}
                      </span>
                    )}
                    <span className="takata-timeline-date">{formatDateTime(c.date_heure)}</span>
                  </div>
                  {renderNotes(c.notes)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Onglet RDV (lecture seule) ───────────────────────────────────────────────

function PortailRdvTab({ rdvList }) {
  return (
    <div className="takata-tab-body">
      <div className="takata-journal-toolbar">
        <span className="takata-journal-count">{rdvList.length} RDV</span>
      </div>
      {rdvList.length === 0 ? (
        <div className="takata-empty-journal">Aucun RDV.</div>
      ) : (
        <div className="takata-rdv-list">
          {rdvList.map((r, i) => {
            const st = STATUTS_RDV[r.statut] || { label: r.statut, color: '#94a3b8' };
            return (
              <div key={i} className="takata-rdv-item">
                <div className="takata-rdv-date">{formatDateTime(r.date_heure)}</div>
                <div className="takata-rdv-meta">
                  <span>{r.type === 'atelier' ? '🏭 Atelier' : '🏠 Domicile'}</span>
                  {r.secteur_geo && <span>📍 {RGPD_REDACTED}</span>}
                  {r.notes && <span>💬 {r.notes}</span>}
                </div>
                <div className="takata-rdv-statut">
                  <span className="takata-badge" style={{ background: fondBadge(st.color), color: st.color }}>{st.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Onglet Infos (lecture seule) ────────────────────────────────────────────

function InfosTab({ dossier }) {
  if (!dossier) return null;

  const Field = ({ label, value }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      <span style={{ fontSize: '0.9rem', color: value ? 'var(--text-primary)' : 'var(--text-muted)' }}>{value || '—'}</span>
    </div>
  );

  const YesNo = ({ label, value }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      <span style={{ fontSize: '0.9rem', color: value ? 'var(--color-success)' : 'var(--text-muted)' }}>{value ? 'Oui' : 'Non'}</span>
    </div>
  );

  const st = STATUTS[dossier.statut] || { label: dossier.statut, color: '#94a3b8' };

  return (
    <div className="takata-tab-body" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <section>
        <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Identification</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px 24px' }}>
          <Field label="VIN" value={dossier.vin} />
          <Field label="Immatriculation" value={dossier.immat} />
          <Field label="Constructeur" value={dossier.marque} />
          <Field label="Modèle" value={dossier.modele} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Statut</span>
            <span style={{ background: fondBadge(st.color), color: st.color, padding: '2px 10px', borderRadius: 6, fontSize: '0.85rem', fontWeight: 600, display: 'inline-block' }}>
              {st.label}
            </span>
          </div>
          <Field label="Concession" value={dossier.concession_nom} />
          <Field label="Date de production" value={formatDate(dossier.date_production)} />
          <Field label="Recall code" value={dossier.recall_code} />
        </div>
      </section>

      <section>
        <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Airbags</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px 24px' }}>
          <YesNo label="Airbag conducteur concerné" value={dossier.driv_airbag_concerned} />
          <YesNo label="Airbag conducteur remplacé" value={dossier.driv_airbag_replaced} />
          <Field label="Date remplacement DRIV" value={formatDate(dossier.replacement_date_driv)} />
          <YesNo label="Airbag passager concerné" value={dossier.pass_airbag_concerned} />
          <YesNo label="Airbag passager remplacé" value={dossier.passenger_airbag_replaced} />
          <Field label="Date remplacement PASS" value={formatDate(dossier.replacement_date_pass)} />
          <YesNo label="Stop drive" value={dossier.stop_drive} />
          <YesNo label="Accident signalé" value={dossier.accident_signale} />
        </div>
      </section>

      <section>
        <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Suivi</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px 24px' }}>
          <Field label="1er contact" value={formatDate(dossier.date_premier_contact)} />
          <Field label="Dernier contact" value={formatDate(dossier.dernier_contact)} />
          <Field label="Prochain RDV" value={formatDate(dossier.prochain_rdv)} />
          <Field label="Enregistré le" value={formatDate(dossier.created_at)} />
          <Field label="Mis à jour le" value={formatDate(dossier.updated_at)} />
        </div>
      </section>
    </div>
  );
}

// ─── Onglet Documents (lecture seule) ────────────────────────────────────────

const TYPES_DOC = {
  ar:        { label: 'AR',         picto: '📩' },
  email:     { label: 'E-mail',     picto: '✉️' },
  log_appel: { label: 'Log appel',  picto: '📞' },
  courrier:  { label: 'Courrier',   picto: '📬' },
  autre:     { label: 'Autre',      picto: '📄' },
};

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' o';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' Ko';
  return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
}

function PortailDocumentsTab({ documents }) {
  return (
    <div className="takata-tab-body">
      <div className="takata-journal-count" style={{ marginBottom: 12 }}>
        {documents.length} document{documents.length !== 1 ? 's' : ''}
      </div>
      {documents.length === 0 ? (
        <div className="takata-empty-journal">Aucun document joint.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {documents.map((doc, i) => {
            const type = TYPES_DOC[doc.type] || { label: doc.type || 'Autre', picto: '📄' };
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: 'var(--bg-muted)', borderRadius: 8, padding: '10px 14px',
                border: '1px solid var(--border-default)',
              }}>
                <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{type.picto}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.nom || 'Fichier sans nom'}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 10 }}>
                    <span>{type.label}</span>
                    {doc.taille ? <span>{formatFileSize(doc.taille)}</span> : null}
                    {doc.created_by_name && <span>par {doc.created_by_name}</span>}
                    <span>{formatDate(doc.created_at)}</span>
                  </div>
                </div>
                {doc.url && (
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ flexShrink: 0, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 6, padding: '5px 12px', fontSize: '0.82rem', color: 'var(--text-primary)', textDecoration: 'none', fontWeight: 500 }}
                  >
                    Télécharger
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PortailConstructeur;
